import { create } from 'zustand';
import { invoke } from '@tauri-apps/api/core';
import { CONSENT_CHANGED_EVENT, choiceOf, readConsent } from './vaultCloudConsent';
import { useSyncRejectionStore } from '../store/syncRejectionStore';

/**
 * The Free plan's cloud-vault cap (Standalone vault card U1, owner decision
 * D2 04.10.2026: 50 items in the cloud on Free; LOCAL features are never
 * gated). The server says how full the cloud copy is in GET /me/vault/consent
 * ({ items, max }; `max` null = unlimited).
 *
 *   capOf(consent)        { items, max, full, nearlyFull, over, cloud }
 *   newItemBlockedByCap   a NEW item would sync into a full cloud vault
 *   useVaultCapStore      the "your free cloud vault is full" card, opened by
 *                         the item editor or by a server refusal (a race)
 *   keepItemsOnDevice     "Keep on this device only": sync off for items the
 *                         cloud never stored, remembered as kept for the cap
 *                         (vault_keep_on_device)
 *   keptCount/uploadKept  the way back once there is room (an upgrade, or
 *                         items removed): exactly the kept items sync again
 *
 * Reading, filling, copying, editing and deleting are never blocked.
 */
export const NEARLY_FULL_RATIO = 0.8;
const TTL_MS = 30000;
// A new item's save waits at most this long for the cap; slower (or offline)
// counts as "not full" and the server's refusal asks instead.
const CHECK_BUDGET_MS = 1500;
let cache = null;

// `invoke` that never throws synchronously (a missing bridge or a stub answers
// null, so a save is never broken by the cap check).
const tryInvoke = (cmd, args) =>
  Promise.resolve()
    .then(() => invoke(cmd, args))
    .catch(() => null);

export function capOf(consent) {
  const items = Number(consent?.items) || 0;
  const maxRaw = Number(consent?.max);
  const max = Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : null;
  return {
    items,
    max,
    full: max != null && items >= max,
    nearlyFull: max != null && items >= Math.ceil(max * NEARLY_FULL_RATIO),
    over: max != null && items > max,
    cloud: choiceOf(consent) === 'cloud',
  };
}

/** The cap, from the server (cached briefly). Null when it cannot be read (offline). */
export async function cloudCap({ fresh = false } = {}) {
  if (!fresh && cache && Date.now() - cache.at < TTL_MS) return cache.value;
  try {
    const value = capOf(await readConsent());
    cache = { at: Date.now(), value };
    return value;
  } catch {
    return null;
  }
}

export function forgetCloudCap() {
  cache = null;
}

/** Settings' "kept on this device" row re-reads its count on this. */
export const KEPT_CHANGED_EVENT = 'vault:cap-kept-changed';
const announceKept = () => typeof window !== 'undefined' && window.dispatchEvent(new Event(KEPT_CHANGED_EVENT));

if (typeof window !== 'undefined') window.addEventListener(CONSENT_CHANGED_EVENT, forgetCloudCap);

/**
 * Whether a new item would go into a full cloud vault. Unknown (offline) is
 * NOT full: the item is saved and, if the server refuses it, the race path
 * asks then — never a blocked save.
 */
export async function newItemBlockedByCap() {
  // Only a device whose vault syncs can hit the cloud cap — a local-only
  // vault never waits on the network to save.
  const status = await tryInvoke('vault_sync_status');
  if (!status?.enrolled) return null;
  const cap = await Promise.race([
    cloudCap({ fresh: true }),
    new Promise((resolve) => setTimeout(() => resolve(null), CHECK_BUDGET_MS)),
  ]);
  return cap && cap.cloud && cap.full ? cap : null;
}

export const useVaultCapStore = create((set) => ({
  open: false,
  ids: [],
  cap: null,
  show: (ids = [], cap = null) => set((s) => ({ open: true, ids: [...new Set([...s.ids, ...ids])], cap: cap || s.cap })),
  setCap: (cap) => set({ cap }),
  /** These ids are handled; the card stays open for any that arrived meanwhile. */
  settle: (done = []) =>
    set((s) => {
      const ids = s.ids.filter((id) => !done.includes(id));
      return ids.length ? { ids } : { open: false, ids: [], cap: null };
    }),
  close: () => set({ open: false, ids: [], cap: null }),
}));

/** "Keep on this device only" for items the server refused. Returns the ids changed. */
export async function keepItemsOnDevice(ids) {
  if (!ids || !ids.length) return [];
  const changed = (await invoke('vault_keep_on_device', { ids })) || [];
  // Their "not synced" notices are answered now.
  changed.forEach((id) => useSyncRejectionStore.getState().clearEntity(id));
  forgetCloudCap();
  announceKept();
  return changed;
}

/** How many items are kept on this device for the cap. 0 when it cannot be read. */
export async function keptCount() {
  return (await tryInvoke('vault_cap_kept_count')) || 0;
}

/** Puts exactly the kept items back to syncing. Returns the ids. */
export async function uploadKept() {
  const ids = (await invoke('vault_upload_kept')) || [];
  forgetCloudCap();
  announceKept();
  return ids;
}
